"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import CustomImage from "../image";
import { renderToString } from "react-dom/server";

function Video({
  id,
  poster,
  title,
  url, // make undefined if using urlSrc instead, use for static url sources
  urlSrc = undefined, // this overrides url, use for dynamic url sources
  gtmEventLabel = null,
  gtmEventCategory = null,
  gtmEventAction = null,
  customClass = undefined,
  imageProps = null,
  disabled = false,
  autoplay = false,
  caption = undefined,
  onVideoPlay = null,
  onVideoPause = null,
  describedBy = null,
  thumbAlt = null,
}, ref) {
  const videoRef = useRef(null);
  const [playVideo, setPlayVideo] = useState(false);
  const [autoPlay, setAutoPlay] = useState(autoplay);
  const seeking = useRef(false);
  const seekingTimeout = useRef(null);

  useEffect(() => {
    if (playVideo) {
      if (autoPlay) {
        if(videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
        setAutoPlay(false);
      }

      // include vide.id amd video.ref when there are multiple videos on the same page
      if (window.hasOwnProperty('currentVideo') && ref && ref.current?.videoId !== window['currentVideo']?.videoId) {
        // stop playing video if another video is played
        window['currentVideo'].stop();
        delete window['currentVideo'];
      }

      if (ref) {
        window['currentVideo'] = ref.current;
      }

      setTimeout(() => {
        videoRef.current?.play();
        videoRef.current?.setAttribute('controls', 'controls');
      }, 333);
      
    } else if (!autoPlay) {
      videoRef.current?.pause();
      videoRef.current?.removeAttribute('controls');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playVideo, ref])

  useEffect(() => {
    if (autoPlay){
      if(videoRef.current){
        videoRef.current.currentTime = 0;
      }
      videoRef.current?.play();
    }
  }, [autoPlay]);

  useEffect(() => {
    const video = videoRef.current;
    video?.addEventListener("contextmenu", (e) => e.preventDefault());

    return () => {
      delete window['currentVideo'];
      setPlayVideo(false);
      video?.removeAttribute("contextmenu", (e) => e.preventDefault());
    };
  }, []);

  useEffect(() => {
    if (caption && videoRef.current.textTracks.length > 0) {
      // videoRef.current.textTracks[0].mode = "showing";
      videoRef.current.textTracks[0].mode = "disabled";
    }
  }, [caption]);

  function handlePlayPause(e) {
    // handle play/pause on click or keydown
    if (e.type === 'keydown') {
      if (e.key !== "Tab") {
        e.preventDefault();
      }

      if (e.key !== "Enter" && e.key !== " ") {
        return;
      }
    }

    // prevent toggling play/pause if the event target is part of the video controls
    if (e.type !== 'keydown' && e.target.tagName === 'VIDEO' && e.target.controls) {
      return;
    }

    !seeking.current && !disabled && setPlayVideo(!playVideo);
  }

  useImperativeHandle(
    ref,
    () => ({
      play() {
        setPlayVideo(true);
      },
      rewind() {
        videoRef.current.currentTime = 0;
      },
      stop() {
        setPlayVideo(false);
        setAutoPlay(false);

        if (autoPlay && videoRef.current) {
          videoRef.current.pause();
        }
      },
      autoplay() {
        setAutoPlay(true);
      },
      get videoId() {
        // pass ref & id when multiple videos on the same page
        return id;
      },
      get playing() {
        return playVideo;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [id, playVideo]
  )

  return (
    <div className={`video${customClass ? ` ${customClass}` : ''}`}>
      <div className="video__player">
        <video
          id={id}
          tabIndex={0}
          className="video__playhead"
          ref={videoRef}
          width="100%"
          title={title}
          poster={poster}
          onPlay={() => {
            onVideoPlay && onVideoPlay();
          }}
          onSeeking={() => {
            seeking.current = true;
          }}
          onSeeked={() => {
            clearTimeout(seekingTimeout.current);
            seekingTimeout.current = setTimeout(() => {
              seeking.current = false;
            }, 500);
          }}
          onPause={() => {
            onVideoPause && onVideoPause();
            setTimeout(() => {
              if (!seeking.current) {
                setPlayVideo(false);
              }
            }, 250);
          }}
          preload="auto"
          onKeyDown={handlePlayPause}
          onClick={handlePlayPause}
          playsInline
          muted={autoPlay}
          data-tracking-initialized
          data-user-started
          src={urlSrc}
          controlsList="nodownload"
          data-gtm-event-category={renderToString(gtmEventCategory)}
          data-gtm-event-action={renderToString(gtmEventAction)}
          data-gtm-event-label={renderToString(gtmEventLabel)}
          aria-describedby={describedBy}
        >
          {!urlSrc && url && <source
            src={url}
            type="video/mp4"
          />}
          {caption && <track
            src={caption}
            label="English"
            kind="subtitles"
            srcLang="en-us"
            
            //default
          />}
          Your browser does not support HTML video.
        </video>
        {!playVideo && <button
          type="button"
          className="video__play-button"
          onClick={handlePlayPause}
          data-gtm-event-category={renderToString(gtmEventCategory)}
          data-gtm-event-action={renderToString(gtmEventAction)}
          data-gtm-event-label={renderToString(gtmEventLabel)}
          aria-label="Play video"
        >
          <CustomImage
            {...{
              alt: thumbAlt || '',
              src: "/images/icons/video-play-icon.svg",
              'data-gtm-event-name': 'Video Start',
              'data-gtm-event-category': 'Video',
              'data-gtm-event-action': 'Start',
              'data-gtm-event-label': gtmEventLabel,
              width: 130,
              height: 130,
              }
            }
            {...imageProps}
          />
        </button>}
      </div>
    </div>
  )
}

export default forwardRef(Video);